import { useRef, useState } from 'react';
import { Upload, Plus, Image as ImageIcon, Square, Sparkles, Wand2, Loader2, Brain, Library } from 'lucide-react';
import { PRESET_COLORS, PRESET_GRADIENTS } from '../lib/canvas-utils';
import { LibraryPicker } from './LibraryPicker';

const Section = ({ title, children }) => (
  <div className="mb-6">
    <h4 className="label-tiny mb-3">{title}</h4>
    {children}
  </div>
);

const HINT_CHIPS = ['polaroid stack', 'magazine cover', 'gallery wall', 'tight grid', 'chaotic scrapbook'];

export const ToolPanel = ({
  onUploadFiles,
  onAddLibraryImage,
  background,
  setBackground,
  onSmartArrange,
  onAiArrange,
  layerCount,
  arrangingMode,
  lastStyleName,
}) => {
  const fileRef = useRef(null);
  const bgFileRef = useRef(null);
  const [styleHint, setStyleHint] = useState('');
  const [addTab, setAddTab] = useState('upload'); // 'upload' | 'library'

  const handleFiles = (files) => {
    const arr = Array.from(files).filter((f) => f.type.startsWith('image/'));
    if (arr.length) onUploadFiles(arr);
  };

  return (
    <aside
      data-testid="tool-panel"
      className="w-full lg:w-72 flex-shrink-0 border-r-2 border-ink bg-paper overflow-y-auto tidy-scroll"
    >
      <div className="p-5">
        {/* Add images: Upload | Library */}
        <Section title="Add images">
          <div
            role="tablist"
            className="grid grid-cols-2 gap-0 mb-3 border-2 border-ink shadow-brutSm"
          >
            <button
              role="tab"
              data-testid="add-tab-upload"
              aria-selected={addTab === 'upload'}
              onClick={() => setAddTab('upload')}
              className={`h-9 flex items-center justify-center gap-1.5 font-outfit text-[11px] font-bold uppercase tracking-wider transition-colors ${
                addTab === 'upload' ? 'bg-lilac' : 'bg-paper hover:bg-sun/30'
              }`}
            >
              <Upload className="h-3.5 w-3.5" strokeWidth={2.5} /> Upload
            </button>
            <button
              role="tab"
              data-testid="add-tab-library"
              aria-selected={addTab === 'library'}
              onClick={() => setAddTab('library')}
              className={`h-9 flex items-center justify-center gap-1.5 font-outfit text-[11px] font-bold uppercase tracking-wider transition-colors border-l-2 border-ink ${
                addTab === 'library' ? 'bg-lilac' : 'bg-paper hover:bg-sun/30'
              }`}
            >
              <Library className="h-3.5 w-3.5" strokeWidth={2.5} /> Library
            </button>
          </div>

          {addTab === 'upload' ? (
            <>
              <div
                data-testid="upload-dropzone"
                onClick={() => fileRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('bg-lilac/40'); }}
                onDragLeave={(e) => e.currentTarget.classList.remove('bg-lilac/40')}
                onDrop={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.remove('bg-lilac/40');
                  handleFiles(e.dataTransfer.files);
                }}
                className="border-2 border-dashed border-ink rounded-sm p-6 flex flex-col items-center justify-center text-center cursor-pointer hover:bg-lilac/30 transition-colors"
              >
                <div className="h-12 w-12 border-2 border-ink bg-lilac shadow-brutSm flex items-center justify-center mb-3">
                  <Upload className="h-5 w-5" strokeWidth={2.5} />
                </div>
                <p className="font-outfit font-bold text-sm">Drop images here</p>
                <p className="font-work text-xs text-ink/60 mt-1">PNG · JPG · unlimited · multi-select</p>
                <input
                  ref={fileRef}
                  data-testid="file-input"
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/webp"
                  multiple
                  hidden
                  onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }}
                />
              </div>
              <button
                data-testid="add-images-btn"
                onClick={() => fileRef.current?.click()}
                className="neo-btn bg-mint w-full h-10 mt-3 text-sm"
              >
                <Plus className="h-4 w-4 mr-1" /> Browse files
              </button>
            </>
          ) : (
            <LibraryPicker onPick={onAddLibraryImage} />
          )}
        </Section>

        {/* Auto-arrange */}
        <Section title="Auto-arrange">
          <div className="grid grid-cols-2 gap-2">
            <button
              data-testid="smart-arrange-btn"
              disabled={!layerCount || arrangingMode !== null}
              onClick={onSmartArrange}
              className="neo-btn bg-peach h-11 text-xs disabled:opacity-40 disabled:cursor-not-allowed disabled:translate-y-0 disabled:shadow-brut"
            >
              {arrangingMode === 'smart' ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Wand2 className="h-4 w-4 mr-1" /> Smart
                </>
              )}
            </button>
            <button
              data-testid="ai-arrange-btn"
              disabled={!layerCount || arrangingMode !== null}
              onClick={() => onAiArrange(styleHint.trim())}
              className="neo-btn bg-lilac h-11 text-xs disabled:opacity-40 disabled:cursor-not-allowed disabled:translate-y-0 disabled:shadow-brut"
            >
              {arrangingMode === 'ai' ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Brain className="h-4 w-4 mr-1" /> AI
                </>
              )}
            </button>
          </div>

          {/* Style hint input */}
          <div className="mt-3">
            <label className="label-tiny mb-1 block">Style hint <span className="font-work normal-case font-normal text-ink/50">(AI only)</span></label>
            <input
              data-testid="style-hint-input"
              type="text"
              value={styleHint}
              onChange={(e) => setStyleHint(e.target.value)}
              placeholder="e.g. polaroid stack, magazine cover…"
              maxLength={80}
              className="neo-input"
            />
            <div className="flex flex-wrap gap-1 mt-2">
              {HINT_CHIPS.map((chip) => (
                <button
                  key={chip}
                  data-testid={`style-chip-${chip.replace(/\s/g, '-')}`}
                  type="button"
                  onClick={() => setStyleHint(chip)}
                  className="border-2 border-ink bg-paper px-2 py-0.5 text-[10px] font-outfit font-bold uppercase tracking-wider hover:bg-sun/40 transition-colors"
                >
                  {chip}
                </button>
              ))}
            </div>
          </div>

          <p className="font-work text-[11px] text-ink/60 mt-3 leading-snug">
            <span className="font-outfit font-bold">Smart</span> shuffles built-in layouts (instant).{' '}
            <span className="font-outfit font-bold">AI</span> uses Claude — add a hint to steer it.
          </p>
          {lastStyleName && (
            <div data-testid="last-style-pill" className="mt-2 inline-flex items-center gap-1 border-2 border-ink bg-sun/40 px-2 py-1">
              <Sparkles className="h-3 w-3" />
              <span className="font-outfit text-[10px] font-bold uppercase tracking-wider">{lastStyleName}</span>
            </div>
          )}
        </Section>

        {/* Background */}
        <Section title="Background">
          <div className="grid grid-cols-4 gap-2 mb-3">
            <BgChip
              testid="bg-type-transparent"
              active={background.type === 'transparent'}
              onClick={() => setBackground({ type: 'transparent' })}
              label="None"
            >
              <div className="h-full w-full checker-bg" />
            </BgChip>
            <BgChip
              testid="bg-type-solid"
              active={background.type === 'solid'}
              onClick={() => setBackground({ type: 'solid', color: background.color || '#FFFFFF' })}
              label="Solid"
            >
              <Square className="h-5 w-5" />
            </BgChip>
            <BgChip
              testid="bg-type-gradient"
              active={background.type === 'gradient'}
              onClick={() => setBackground({
                type: 'gradient',
                from: background.from || PRESET_GRADIENTS[0].from,
                to: background.to || PRESET_GRADIENTS[0].to,
                angle: background.angle ?? PRESET_GRADIENTS[0].angle,
              })}
              label="Grad"
            >
              <Sparkles className="h-5 w-5" />
            </BgChip>
            <BgChip
              testid="bg-type-image"
              active={background.type === 'image'}
              onClick={() => bgFileRef.current?.click()}
              label="Image"
            >
              <ImageIcon className="h-5 w-5" />
            </BgChip>
            <input
              ref={bgFileRef}
              data-testid="bg-file-input"
              type="file"
              accept="image/*"
              hidden
              onChange={async (e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                const reader = new FileReader();
                reader.onload = () => setBackground({ type: 'image', src: reader.result });
                reader.readAsDataURL(f);
                e.target.value = '';
              }}
            />
          </div>

          {background.type === 'solid' && (
            <div data-testid="bg-solid-controls">
              <div className="grid grid-cols-5 gap-2">
                {PRESET_COLORS.map((c) => (
                  <button
                    key={c}
                    data-testid={`color-swatch-${c}`}
                    onClick={() => setBackground({ type: 'solid', color: c })}
                    aria-label={`Color ${c}`}
                    className={`h-9 w-full border-2 border-ink shadow-brutSm rounded-sm transition-transform hover:-translate-y-0.5 ${background.color === c ? 'ring-2 ring-offset-2 ring-ink' : ''}`}
                    style={{ background: c }}
                  />
                ))}
              </div>
              <label className="mt-3 flex items-center gap-2">
                <span className="font-work text-xs text-ink/70">Custom</span>
                <input
                  data-testid="bg-color-picker"
                  type="color"
                  value={background.color || '#FFFFFF'}
                  onChange={(e) => setBackground({ type: 'solid', color: e.target.value })}
                  className="h-9 w-9 border-2 border-ink rounded-sm cursor-pointer"
                />
                <span className="font-mono text-xs">{(background.color || '#FFFFFF').toUpperCase()}</span>
              </label>
            </div>
          )}

          {background.type === 'gradient' && (
            <div data-testid="bg-gradient-controls">
              <div className="grid grid-cols-3 gap-2 mb-3">
                {PRESET_GRADIENTS.map((g, i) => (
                  <button
                    key={i}
                    data-testid={`gradient-preset-${i}`}
                    onClick={() => setBackground({ type: 'gradient', ...g })}
                    aria-label={`Gradient ${i}`}
                    className="h-12 border-2 border-ink shadow-brutSm rounded-sm hover:-translate-y-0.5 transition-transform"
                    style={{ background: `linear-gradient(${g.angle}deg, ${g.from}, ${g.to})` }}
                  />
                ))}
              </div>
              <div className="flex items-center gap-2 mb-2">
                <span className="font-work text-xs w-10 text-ink/70">From</span>
                <input
                  data-testid="grad-from-picker"
                  type="color"
                  value={background.from}
                  onChange={(e) => setBackground({ ...background, from: e.target.value })}
                  className="h-8 w-10 border-2 border-ink rounded-sm cursor-pointer"
                />
                <span className="font-work text-xs w-8 text-ink/70 ml-2">To</span>
                <input
                  data-testid="grad-to-picker"
                  type="color"
                  value={background.to}
                  onChange={(e) => setBackground({ ...background, to: e.target.value })}
                  className="h-8 w-10 border-2 border-ink rounded-sm cursor-pointer"
                />
              </div>
              <label className="flex items-center gap-3 mt-2">
                <span className="font-work text-xs text-ink/70 w-10">Angle</span>
                <input
                  data-testid="grad-angle-slider"
                  type="range" min="0" max="360"
                  value={background.angle}
                  onChange={(e) => setBackground({ ...background, angle: Number(e.target.value) })}
                  className="flex-1"
                />
                <span className="font-mono text-xs w-10 text-right">{background.angle}°</span>
              </label>
            </div>
          )}

          {background.type === 'image' && background.src && (
            <div data-testid="bg-image-preview" className="border-2 border-ink rounded-sm overflow-hidden h-24 bg-canvasbg">
              <img src={background.src} alt="bg preview" className="h-full w-full object-cover" />
            </div>
          )}
        </Section>

        {/* Tip */}
        <div className="border-2 border-ink bg-sun/40 p-3 mt-2">
          <p className="font-work text-xs leading-relaxed">
            <strong className="font-outfit">Shortcuts:</strong> Ctrl/⌘+Z undo, Ctrl/⌘+Shift+Z redo. Hold Shift while rotating to snap.
          </p>
        </div>
      </div>
    </aside>
  );
};

const BgChip = ({ active, onClick, children, label, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`flex flex-col items-center justify-center gap-1 h-14 border-2 border-ink rounded-sm transition-all overflow-hidden ${active ? 'bg-lilac shadow-brut -translate-y-0.5' : 'bg-paper shadow-brutSm hover:-translate-y-0.5'}`}
  >
    <div className="h-6 w-6 flex items-center justify-center overflow-hidden border border-ink">
      {children}
    </div>
    <span className="font-outfit text-[10px] font-bold uppercase tracking-wider">{label}</span>
  </button>
);
